import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Cpu,
  Download,
  FileText,
  FlaskConical,
  Loader2,
  Mic,
  MonitorSpeaker,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Search,
  Sparkles,
  Square,
  User,
  WandSparkles,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Skeleton } from './ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import ModuleShell from './callcopilot/ModuleShell';
import { StepperSection } from './callcopilot/StepperSection';
import { AgentStatusChip } from './callcopilot/AgentStatusChip';
import { EmptyState } from './callcopilot/EmptyState';
import { SectionLabel } from './callcopilot/SectionLabel';
import SessionTimer from './callcopilot/SessionTimer';

const AGENT_URL = 'http://127.0.0.1:8765';
const WS_URL = 'ws://127.0.0.1:8765';
const MAX_CONTEXT = 12;
const CUSTOMER_BUFFER_MS = 6500;
const CUSTOMER_OBJECTION_BUFFER_MS = 4500;
const CUSTOMER_BUFFER_MAX_CHARS = 720;
const LIVE_MERGE_WINDOW_MS = 8500;
const LIVE_MERGE_FLUSH_MS = 1800;
const REALTIME_SIGNAL_MIN_DELAY_MS = 90 * 1000;
const REALTIME_SIGNAL_MIN_TURNS = 4;
const TRANSCRIPTION_ENGINE = 'google';
const OBJECTION_TRIGGER_PATTERN = /\b(caro|preço|preco|valor|orçamento|orcamento|sem verba|diretoria|diretor|sócio|socio|chefe|financeiro|comitê|comite|aprovar|aprovação|aprovacao|consultar|validar|ver com|agora não|agora nao|mais pra frente|depois|sem tempo|concorrente|já tenho|ja tenho|outra solução|outra solucao|fornecedor atual)\b/i;
const OBJECTIVES_BY_ROLE = {
  seller: ['qualification', 'discovery', 'negotiation', 'renewal', 'expansion'],
  buyer: ['evaluation', 'procurement', 'negotiation'],
  manager: ['meeting', 'planning', 'decision'],
  leader: ['one_on_one', 'feedback', 'development'],
  report: ['execution', 'planning', 'clarification'],
};
const OBJECTIVE_LABELS = {
  negotiation: 'Negociação',
  qualification: 'Qualificação',
  discovery: 'Descoberta de necessidades',
  renewal: 'Renovação',
  expansion: 'Expansão de conta',
  evaluation: 'Avaliação de fornecedor',
  procurement: 'Compras e contratação',
  meeting: 'Reunião',
  planning: 'Planejamento',
  decision: 'Tomada de decisão',
  one_on_one: '1:1 / Condução',
  feedback: 'Feedback',
  development: 'Desenvolvimento',
  execution: 'Execução',
  clarification: 'Alinhamento de execução',
};
const ROLE_LABELS = {
  seller: 'Vendedor',
  buyer: 'Comprador',
  manager: 'Gestor',
  leader: 'Líder',
  report: 'Liderado',
};
const SESSION_STEPS = [
  { id: 'prepare', label: 'Preparar', icon: ClipboardList },
  { id: 'live', label: 'Ao vivo', icon: Radio },
  { id: 'feedback', label: 'Feedback', icon: FileText },
];
const EMERALD_CTA_CLASSES = 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-2 focus-visible:ring-emerald-500/40';

function SpinningLoaderIcon({ className }) {
  return <Loader2 className={`animate-spin ${className || ''}`} />;
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
}

function normalizeSpeakerForRole(speaker, role) {
  if (role !== 'buyer') return speaker;
  if (speaker === 'seller') return 'customer';
  if (speaker === 'customer') return 'seller';
  return speaker;
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-emerald-100">
      <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function CallCopilot({ api, onNavigate }) {
  const [agentStatus, setAgentStatus] = useState('checking');
  const [agentDetail, setAgentDetail] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessionState, setSessionState] = useState('idle');
  const [events, setEvents] = useState([]);
  const [cards, setCards] = useState([]);
  const [activePopup, setActivePopup] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [audioDevices, setAudioDevices] = useState({ microphones: [], speakers: [] });
  const [sellerDeviceId, setSellerDeviceId] = useState('');
  const [customerDeviceId, setCustomerDeviceId] = useState('');
  const [meetingId, setMeetingId] = useState(null);
  const [leadOptions, setLeadOptions] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);
  const [objective, setObjective] = useState('negotiation');
  const [role, setRole] = useState('seller');
  const [meetingScope, setMeetingScope] = useState('');
  const [playbooks, setPlaybooks] = useState({ objectives: [], objectives_by_role: {}, roles: [], playbooks: [] });
  const [generatedPlaybook, setGeneratedPlaybook] = useState(null);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState('');
  const [preparingPlaybook, setPreparingPlaybook] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState('idle');
  const [savingContext, setSavingContext] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const wsRef = useRef(null);
  const eventSourceRef = useRef(null);
  const eventFallbackTimerRef = useRef(null);
  const meetingIdRef = useRef(null);
  const transcriptKeysRef = useRef(new Set());
  const liveMergeRef = useRef({ customer: null, seller: null });
  const liveMergeTimersRef = useRef({ customer: null, seller: null });
  const seenTextsRef = useRef(new Set());
  const customerBufferRef = useRef({ parts: [], baseEvent: null });
  const customerBufferTimerRef = useRef(null);
  const liveTranscriptRef = useRef(null);
  const sessionStartedAtRef = useRef(null);
  const conversationTurnCountRef = useRef(0);
  const isFinishingRef = useRef(false);

  const customerContext = useMemo(
    () => events.slice(0, MAX_CONTEXT).map((event) => ({
      speaker: event.speaker,
      text: event.text,
      at: event.created_at,
    })),
    [events]
  );

  const allowedObjectives = playbooks.objectives_by_role?.[role] || OBJECTIVES_BY_ROLE[role] || ['negotiation'];
  const objectiveLabel = (key) => playbooks.objectives.find((item) => item.key === key)?.name || OBJECTIVE_LABELS[key] || key;
  const roleLabel = (key) => playbooks.roles.find((item) => item.key === key)?.name || ROLE_LABELS[key] || key;
  const availablePlaybooks = useMemo(
    () => (playbooks.playbooks || []).filter((item) => item.license_id && item.role === role && item.objective === objective),
    [playbooks.playbooks, role, objective]
  );
  const selectedPlaybook = useMemo(
    () => availablePlaybooks.find((item) => item.id === selectedPlaybookId),
    [availablePlaybooks, selectedPlaybookId]
  );
  const effectiveMeetingScope = meetingScope.trim()
    || selectedPlaybook?.description?.trim()
    || selectedPlaybook?.guidance?.trim()
    || `Conversa de ${objectiveLabel(objective).toLowerCase()} com foco em perguntas, decisões e próximos passos.`;
  const selectedLead = useMemo(() => leadOptions.find((lead) => lead.id === selectedLeadId), [leadOptions, selectedLeadId]);
  const filteredLeads = useMemo(() => {
    const query = leadSearch.trim().toLowerCase();
    if (!query) return leadOptions.slice(0, 8);
    return leadOptions.filter((lead) => `${lead.name || ''} ${lead.company || ''} ${lead.email || ''}`.toLowerCase().includes(query)).slice(0, 8);
  }, [leadOptions, leadSearch]);
  const sessionLocked = sessionState !== 'idle' || Boolean(sessionId);

  useEffect(() => {
    if (!availablePlaybooks.some((item) => item.id === selectedPlaybookId)) {
      setSelectedPlaybookId(availablePlaybooks.length === 1 ? availablePlaybooks[0].id : '');
      setGeneratedPlaybook(null);
    }
  }, [availablePlaybooks, selectedPlaybookId]);

  const fetchPlaybooks = async () => {
    try {
      const res = await api.get('/call-copilot/playbooks');
      setPlaybooks(res.data || { objectives: [], roles: [], playbooks: [] });
    } catch {
      setPlaybooks({ objectives: [], objectives_by_role: {}, roles: [], playbooks: [] });
    }
  };

  const fetchLeadOptions = async () => {
    try {
      const res = await api.get('/leads?page=1&limit=100&sort_by=name&sort_order=asc');
      setLeadOptions(res.data?.items || []);
    } catch {
      setLeadOptions([]);
    }
  };

  const downloadAgent = async () => {
    try {
      const res = await api.get('/call-copilot/agent/download', {
        params: { package: 'installer', platform: 'windows' },
        responseType: 'blob',
      });
      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'nexosynx-call-copilot-suite.zip';
      const isInstaller = filename.toLowerCase().endsWith('.exe');
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: isInstaller ? 'application/vnd.microsoft.portable-executable' : 'application/zip',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isInstaller
        ? 'Instalador do agente local baixado'
        : 'Pacote temporário do agente baixado. Execute o start-windows.bat.');
    } catch (error) {
      let detail = error?.response?.data?.detail;
      if (!detail && error?.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await error.response.data.text()).detail;
        } catch {
          detail = null;
        }
      }
      toast.error(detail || 'Não foi possível baixar o agente local');
    }
  };

  const checkAgent = async () => {
    setAgentStatus('checking');
    try {
      const res = await fetch(`${AGENT_URL}/health`);
      if (!res.ok) throw new Error('agent offline');
      const data = await res.json();
      await fetchAudioDevices();
      setAgentStatus('online');
      setAgentDetail(data.version ? `Agente local ${data.version}` : 'Agente local pronto');
      if (sessionId) {
        const sessionRes = await fetch(`${AGENT_URL}/sessions/${sessionId}`);
        if (sessionRes.status === 404) {
          clearLocalSession('Sessão local expirada. Inicie uma nova sessão.');
        }
      }
    } catch {
      setAgentStatus('offline');
      setAgentDetail('Inicie o agente local em 127.0.0.1:8765');
    }
  };

  const fetchAudioDevices = async () => {
    try {
      const res = await fetch(`${AGENT_URL}/devices`);
      if (!res.ok) throw new Error('devices unavailable');
      const data = await res.json();
      const microphones = data.microphones || [];
      const loopbacks = microphones.filter((device) => device.loopback);
      setAudioDevices({ microphones, speakers: data.speakers || [] });
      if (!sellerDeviceId) {
        const defaultMic = microphones.find((device) => device.default && !device.loopback) || microphones.find((device) => !device.loopback);
        if (defaultMic) setSellerDeviceId(defaultMic.id);
      }
      if (!customerDeviceId) {
        const defaultLoopback = loopbacks.find((device) => device.default) || loopbacks[0];
        if (defaultLoopback) setCustomerDeviceId(defaultLoopback.id);
      }
    } catch {
      setAudioDevices({ microphones: [], speakers: [] });
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    fetchLeadOptions();
    checkAgent();
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (wsRef.current) wsRef.current.close();
      if (eventFallbackTimerRef.current) clearTimeout(eventFallbackTimerRef.current);
      if (customerBufferTimerRef.current) clearTimeout(customerBufferTimerRef.current);
      Object.values(liveMergeTimersRef.current).forEach((timer) => timer && clearTimeout(timer));
    };
  }, []);

  const clearLocalSession = (message) => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    if (wsRef.current) wsRef.current.close();
    if (eventFallbackTimerRef.current) clearTimeout(eventFallbackTimerRef.current);
    if (customerBufferTimerRef.current) clearTimeout(customerBufferTimerRef.current);
    eventFallbackTimerRef.current = null;
    customerBufferTimerRef.current = null;
    customerBufferRef.current = { parts: [], baseEvent: null };
    setSessionId(null);
    setSessionState('idle');
    setAgentStatus('online');
    if (message) setAgentDetail(message);
  };

  const startNewAgenda = () => {
    if (sessionLocked) return;
    setEvents([]);
    setCards([]);
    setActivePopup(null);
    seenTextsRef.current.clear();
    transcriptKeysRef.current.clear();
    setMeetingScope('');
    setGeneratedPlaybook(null);
    setSelectedPlaybookId('');
    setLeadSearch('');
    setFeedbackStatus('idle');
    setAgentDetail('Prepare a próxima agenda para iniciar uma nova sessão');
    toast.success('Nova agenda pronta');
  };

  const persistTranscript = async (event) => {
    const activeMeetingId = meetingIdRef.current;
    const text = (event.text || '').trim();
    if (!activeMeetingId || !text) return;
    const key = `${event.speaker}:${event.created_at}:${text}`;
    if (transcriptKeysRef.current.has(key)) return;
    transcriptKeysRef.current.add(key);
    setSavingContext(true);
    try {
      await api.post(`/call-copilot/meetings/${activeMeetingId}/transcripts`, {
        agent_session_id: event.session_id || sessionId,
        speaker: event.speaker,
        text,
        confidence: event.confidence,
        source: event.source,
        platform: event.platform,
        started_at: event.started_at,
        ended_at: event.ended_at,
        created_at: event.created_at,
      });
    } catch {
      setAgentDetail('Falha ao salvar uma fala no contexto da reunião');
    } finally {
      setSavingContext(false);
    }
  };

  const queueTranscriptPersistence = (event) => {
    const speaker = event.speaker || 'unknown';
    const previous = liveMergeRef.current[speaker];
    const previousAt = previous ? new Date(previous.created_at || previous.ended_at || 0).getTime() : 0;
    const currentAt = new Date(event.created_at || event.ended_at || 0).getTime();
    const canMerge = previous && previous.speaker === speaker
      && (!Number.isFinite(currentAt) || !Number.isFinite(previousAt) || currentAt - previousAt <= LIVE_MERGE_WINDOW_MS)
      && !/[.!?…]\s*$/.test(previous.text || '');
    const merged = canMerge
      ? { ...event, ...previous, text: `${previous.text} ${event.text}`.replace(/\s+/g, ' ').trim(), ended_at: event.ended_at, created_at: previous.created_at }
      : event;
    liveMergeRef.current[speaker] = merged;
    if (liveMergeTimersRef.current[speaker]) clearTimeout(liveMergeTimersRef.current[speaker]);
    liveMergeTimersRef.current[speaker] = setTimeout(() => {
      const pending = liveMergeRef.current[speaker];
      liveMergeRef.current[speaker] = null;
      liveMergeTimersRef.current[speaker] = null;
      if (pending) persistTranscript(pending);
    }, LIVE_MERGE_FLUSH_MS);
  };

  const flushLiveTranscriptPersistence = async () => {
    const pending = Object.entries(liveMergeRef.current)
      .map(([speaker, event]) => {
        if (liveMergeTimersRef.current[speaker]) clearTimeout(liveMergeTimersRef.current[speaker]);
        liveMergeTimersRef.current[speaker] = null;
        liveMergeRef.current[speaker] = null;
        return event;
      })
      .filter(Boolean);
    await Promise.all(pending.map((event) => persistTranscript(event)));
  };

  const persistCard = async (card) => {
    const activeMeetingId = meetingIdRef.current;
    if (card.session_id) {
      fetch(`${AGENT_URL}/sessions/${card.session_id}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phrase: card.phrase,
          objection_key: card.objection_key,
          pain_diagnosis: card.pain_diagnosis,
          confidence: card.confidence,
          fixed_response: card.fixed_response,
          raw_reason: card.raw_reason,
          created_at: card.created_at,
        }),
      }).catch(() => {});
    }
    if (!activeMeetingId) return;
    try {
      await api.post(`/call-copilot/meetings/${activeMeetingId}/cards`, {
        agent_session_id: card.session_id || sessionId,
        phrase: card.phrase,
        objection_key: card.objection_key,
        pain_diagnosis: card.pain_diagnosis,
        confidence: card.confidence,
        fixed_response: card.fixed_response,
        raw_reason: card.raw_reason,
        signal_type: card.signal_type,
        topic_key: card.topic_key,
        topic: card.topic,
        suggested_question: card.suggested_question,
        suggested_action: card.suggested_action,
        priority: card.priority,
        evidence: card.evidence,
        created_at: card.created_at,
      });
    } catch {
      setAgentDetail('Card exibido, mas não consegui salvar no histórico');
    }
  };

  const finishMeeting = async () => {
    const activeMeetingId = meetingIdRef.current;
    if (!activeMeetingId) return;
    setIsFinishing(true);
    setFeedbackStatus('running');
    try {
      const res = await api.post(`/call-copilot/meetings/${activeMeetingId}/finish`);
      setFeedbackStatus(res.data?.analysis_status || 'ready');
      toast.success('Feedback da reunião gerado');
      setEvents([]);
      setCards([]);
      setActivePopup(null);
      seenTextsRef.current.clear();
      transcriptKeysRef.current.clear();
      setMeetingScope('');
      setGeneratedPlaybook(null);
      setSelectedPlaybookId('');
      if (onNavigate) onNavigate('call-feedback');
    } catch (error) {
      setFeedbackStatus('failed');
      const message = error?.response?.data?.detail || 'Não consegui gerar o feedback da reunião';
      toast.error(message);
    } finally {
      meetingIdRef.current = null;
      setMeetingId(null);
      setIsFinishing(false);
      isFinishingRef.current = false;
      sessionStartedAtRef.current = null;
      conversationTurnCountRef.current = 0;
    }
  };

  const classifyCustomerText = async (text, event) => {
    if (isFinishingRef.current) return;
    const expectedSpeaker = role === 'seller' ? 'customer' : role === 'buyer' ? 'seller' : event.speaker;
    if (['seller', 'buyer'].includes(role) && event.speaker !== expectedSpeaker) return;
    const normalizedText = (text || '').trim();
    if (normalizedText.length < 8) return;
    const classifySpeaker = role === 'seller' ? 'customer' : role === 'buyer' ? 'seller' : event.speaker;
    const dedupeKey = `${classifySpeaker}:${normalizedText.toLowerCase()}`;
    if (seenTextsRef.current.has(dedupeKey)) return;
    seenTextsRef.current.add(dedupeKey);

    setClassifying(true);
    try {
      const res = await api.post('/call-copilot/classify', {
        session_id: event.session_id || sessionId,
        speaker: classifySpeaker,
        phrase: normalizedText,
        platform: event.platform || 'desktop',
        context_window: customerContext,
        objective,
        role,
        meeting_scope: meetingScope,
        agenda_topics: generatedPlaybook?.topics || [],
      });
      const result = res.data;
      if (result?.should_show_card && !isFinishingRef.current) {
        const nextCard = {
          id: `${Date.now()}-${Math.random()}`,
          phrase: normalizedText,
          session_id: event.session_id || sessionId,
          created_at: new Date().toISOString(),
          ...result,
        };
        setActivePopup(nextCard);
        setCards((prev) => [nextCard, ...prev].slice(0, 12));
        persistCard(nextCard);
      }
    } catch (error) {
      const message = error?.response?.data?.detail || 'Erro ao classificar fala do cliente';
      toast.error(message);
    } finally {
      setClassifying(false);
    }
  };

  const flushCustomerBuffer = () => {
    if (customerBufferTimerRef.current) {
      clearTimeout(customerBufferTimerRef.current);
      customerBufferTimerRef.current = null;
    }
    const buffer = customerBufferRef.current;
    const text = buffer.parts.join(' ').replace(/\s+/g, ' ').trim();
    const baseEvent = buffer.baseEvent;
    customerBufferRef.current = { parts: [], baseEvent: null };
    if (text && baseEvent) classifyCustomerText(text, baseEvent);
  };

  const enqueueCustomerForClassification = (event) => {
    if (isFinishingRef.current) return;
    const expectedSpeaker = role === 'seller' ? 'customer' : role === 'buyer' ? 'seller' : event.speaker;
    if (['seller', 'buyer'].includes(role) && event.speaker !== expectedSpeaker) return;
    const text = (event.text || '').trim();
    if (text.length < 8) return;
    const elapsed = sessionStartedAtRef.current ? Date.now() - sessionStartedAtRef.current : 0;
    if (elapsed < REALTIME_SIGNAL_MIN_DELAY_MS || conversationTurnCountRef.current < REALTIME_SIGNAL_MIN_TURNS) return;
    const current = customerBufferRef.current;
    current.parts.push(text);
    if (!current.baseEvent) current.baseEvent = event;

    const combined = current.parts.join(' ');
    const likelyObjection = OBJECTION_TRIGGER_PATTERN.test(combined);
    const looksComplete = /[.!?…]\s*$/.test(text) && combined.length >= 60;
    const maxed = combined.length >= CUSTOMER_BUFFER_MAX_CHARS;

    if (customerBufferTimerRef.current) clearTimeout(customerBufferTimerRef.current);
    if (looksComplete || maxed) {
      flushCustomerBuffer();
      return;
    }
    customerBufferTimerRef.current = setTimeout(
      flushCustomerBuffer,
      likelyObjection ? CUSTOMER_OBJECTION_BUFFER_MS : CUSTOMER_BUFFER_MS
    );
  };

  const connectEvents = (id) => {
    if (eventSourceRef.current) eventSourceRef.current.close();
    if (wsRef.current) wsRef.current.close();
    if (eventFallbackTimerRef.current) clearTimeout(eventFallbackTimerRef.current);
    eventFallbackTimerRef.current = null;
    const handleIncomingEvent = (event) => {
      if (event.type && event.type !== 'transcript') {
        if (event.type === 'heartbeat') return;
        if (event.message) {
          setAgentDetail(event.message);
          if (event.level === 'error') toast.error(event.message);
        }
        return;
      }
      const normalized = {
        session_id: event.session_id || id,
        speaker: normalizeSpeakerForRole(event.speaker || 'unknown', role),
        text: event.text || '',
        confidence: event.confidence ?? null,
        source: event.source || TRANSCRIPTION_ENGINE,
        platform: event.platform || 'desktop',
        started_at: event.started_at,
        ended_at: event.ended_at,
        created_at: event.created_at || new Date().toISOString(),
      };
      if (['customer', 'seller'].includes(normalized.speaker)) conversationTurnCountRef.current += 1;
      setEvents((prev) => {
        const ordered = [...prev, normalized].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        const compacted = [];
        ordered.forEach((item) => {
          const last = compacted[compacted.length - 1];
          const gap = last ? new Date(item.created_at).getTime() - new Date(last.created_at).getTime() : Infinity;
          const canMerge = last && last.speaker === item.speaker && gap <= LIVE_MERGE_WINDOW_MS && !/[.!?…]\s*$/.test(last.text || '');
          if (canMerge) {
            compacted[compacted.length - 1] = { ...last, text: `${last.text} ${item.text}`.replace(/\s+/g, ' ').trim(), ended_at: item.ended_at };
          } else {
            compacted.push(item);
          }
        });
        return compacted.slice(-80).reverse();
      });
      queueTranscriptPersistence(normalized);
      if (['customer', 'seller'].includes(normalized.speaker)) enqueueCustomerForClassification(normalized);
      else flushCustomerBuffer();
    };

    const eventSource = new EventSource(`${AGENT_URL}/sessions/${id}/events-sse`);
    eventSource.onopen = () => {
      if (eventFallbackTimerRef.current) clearTimeout(eventFallbackTimerRef.current);
      eventFallbackTimerRef.current = null;
      setAgentDetail('Conectado ao stream SSE do agente');
    };
    eventSource.onmessage = (message) => {
      try {
        handleIncomingEvent(JSON.parse(message.data));
      } catch {
        // Ignore malformed local-agent SSE events.
      }
    };
    eventSource.onerror = () => {
      setAgentDetail('SSE reconectando ao agente local...');
      if (eventFallbackTimerRef.current) return;
      eventFallbackTimerRef.current = setTimeout(() => {
        if (eventSource.readyState !== EventSource.OPEN) {
          eventSource.close();
          if (eventSourceRef.current === eventSource) eventSourceRef.current = null;
          connectEventsWebSocket(id, handleIncomingEvent);
        }
      }, 4000);
    };
    eventSourceRef.current = eventSource;
  };

  const connectEventsWebSocket = (id, handleIncomingEvent) => {
    if (wsRef.current) wsRef.current.close();
    const ws = new WebSocket(`${WS_URL}/sessions/${id}/events`);
    ws.onopen = () => setAgentDetail('Conectado ao stream do agente');
    ws.onmessage = (message) => {
      try {
        handleIncomingEvent(JSON.parse(message.data));
      } catch {
        // Ignore malformed local-agent events.
      }
    };
    ws.onclose = () => {
      if (sessionState === 'recording') setAgentDetail('Stream encerrado pelo agente');
    };
    wsRef.current = ws;
  };

  const preparePlaybook = async () => {
    if (role === 'seller' && !selectedLeadId) {
      toast.error('Selecione um lead para usar o modo vendedor');
      return null;
    }
    setPreparingPlaybook(true);
    try {
      const res = await api.post('/call-copilot/prepare', {
        objective,
        role,
        meeting_scope: effectiveMeetingScope,
        lead_id: role === 'seller' ? selectedLeadId : null,
        playbook_id: selectedPlaybookId || null,
      });
      setGeneratedPlaybook(res.data);
      toast.success('Playbook preparado pela IA');
      return res.data;
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível preparar o playbook');
      return null;
    } finally {
      setPreparingPlaybook(false);
    }
  };

  const startSession = async () => {
    if (sessionLocked) {
      toast.error('Finalize a sessão atual antes de iniciar outra agenda');
      return;
    }
    let startedAgentSessionId = null;
    try {
      const prepared = generatedPlaybook || await preparePlaybook();
      if (!prepared) return;
      let localAgentConfig = {};
      if (TRANSCRIPTION_ENGINE === 'google') {
        try {
          const cfg = await api.get('/call-copilot/local-agent-config');
          localAgentConfig = cfg.data || {};
        } catch {
          localAgentConfig = {};
        }
      }
      const res = await fetch(`${AGENT_URL}/sessions/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcription_engine: TRANSCRIPTION_ENGINE,
          language: 'pt-BR',
          platform: 'desktop',
          google_speech_credentials_json: localAgentConfig.google_speech_configured
            ? localAgentConfig.google_speech_credentials_json
            : null,
          seller_device_id: sellerDeviceId ? Number(sellerDeviceId) : null,
          customer_device_id: customerDeviceId ? Number(customerDeviceId) : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      startedAgentSessionId = data.session_id;
      const meetingRes = await api.post('/call-copilot/meetings', {
        platform: 'desktop',
        transcription_engine: TRANSCRIPTION_ENGINE,
        agent_session_id: data.session_id,
        lead_id: selectedLeadId || null,
        objective,
        role,
        meeting_scope: effectiveMeetingScope,
        agenda_topics: prepared.topics || [],
        generated_playbook: prepared,
        playbook_id: prepared.playbook_id || selectedPlaybookId || null,
      });
      const meeting = meetingRes.data;
      meetingIdRef.current = meeting.id;
      sessionStartedAtRef.current = Date.now();
      conversationTurnCountRef.current = 0;
      isFinishingRef.current = false;
      setMeetingId(meeting.id);
      setFeedbackStatus('pending');
      transcriptKeysRef.current.clear();
      setSessionId(data.session_id);
      setSessionState('recording');
      setAgentStatus('recording');
      setEvents([]);
      setCards([]);
      setActivePopup(null);
      seenTextsRef.current.clear();
      liveMergeRef.current = { customer: null, seller: null };
      Object.values(liveMergeTimersRef.current).forEach((timer) => timer && clearTimeout(timer));
      liveMergeTimersRef.current = { customer: null, seller: null };
      customerBufferRef.current = { parts: [], baseEvent: null };
      if (customerBufferTimerRef.current) clearTimeout(customerBufferTimerRef.current);
      customerBufferTimerRef.current = null;
      connectEvents(data.session_id);
      toast.success('Copiloto iniciado');
    } catch (error) {
      if (startedAgentSessionId) {
        fetch(`${AGENT_URL}/sessions/${startedAgentSessionId}/stop`, { method: 'POST' }).catch(() => {});
      }
      const detail = error?.response?.data?.detail || error?.message || 'Não consegui iniciar o agente local';
      toast.error(detail.length > 180 ? `${detail.slice(0, 180)}...` : detail);
      checkAgent();
    }
  };

  const pauseSession = async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${AGENT_URL}/sessions/${sessionId}/pause`, { method: 'POST' });
      if (res.status === 404) {
        clearLocalSession('Sessão local expirada. Inicie uma nova sessão.');
        toast.error('Sessão local expirada. Clique em Iniciar.');
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      setSessionState('paused');
      setAgentStatus('paused');
    } catch {
      toast.error('Erro ao pausar sessão');
    }
  };

  const stopSession = async () => {
    if (!sessionId) return;
    setIsFinishing(true);
    isFinishingRef.current = true;
    setSessionState('finishing');
    try {
      flushCustomerBuffer();
      const res = await fetch(`${AGENT_URL}/sessions/${sessionId}/stop`, { method: 'POST' });
      if (res.status !== 404 && !res.ok) throw new Error(await res.text());
      await flushLiveTranscriptPersistence();
      clearLocalSession(res.status === 404 ? 'Sessão local já não existia no agente.' : null);
      setAgentStatus('online');
      await finishMeeting();
    } catch {
      setIsFinishing(false);
      isFinishingRef.current = false;
      setSessionState(sessionId ? 'recording' : 'idle');
      toast.error('Erro ao finalizar sessão');
    }
  };

  const emitTestTranscript = async (speaker) => {
    if (!sessionId) {
      toast.error('Inicie uma sessão antes de testar');
      return;
    }
    const text = speaker === 'customer'
      ? 'Eu gostei da solução, mas agora está caro para o nosso orçamento.'
      : 'Vou explicar como o investimento se paga com o ganho mensal.';
    try {
      const res = await fetch(`${AGENT_URL}/sessions/${sessionId}/emit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          speaker,
          text,
          confidence: 0.98,
          source: 'manual-test',
        }),
      });
      if (res.status === 404) {
        clearLocalSession('Sessão local expirada. Inicie uma nova sessão.');
        toast.error('Sessão local expirada. Clique em Iniciar novamente.');
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      toast.success(speaker === 'customer' ? 'Teste de fala do cliente enviado' : 'Teste de fala do vendedor enviado');
    } catch {
      toast.error('Erro ao enviar teste para o agente local');
    }
  };

  const latestCard = cards[0];
  const phase = sessionState === 'idle' && !sessionId ? 'prepare' : sessionState === 'finishing' ? 'finishing' : 'live';
  const chronologicalEvents = [...events].reverse();
  const persistenceChip = savingContext
    ? { label: 'Salvando contexto…', classes: 'animate-pulse bg-slate-100 text-slate-600' }
    : feedbackStatus === 'running'
      ? { label: 'Gerando feedback…', classes: 'bg-violet-50 text-violet-700' }
      : feedbackStatus === 'failed'
        ? { label: 'Feedback falhou', classes: 'bg-rose-50 text-rose-700' }
        : feedbackStatus === 'ready'
          ? { label: 'Feedback pronto', classes: 'bg-emerald-50 text-emerald-700' }
          : null;
  const startPendencies = [];
  if (agentStatus === 'offline') startPendencies.push('Agente offline');
  if (role === 'seller' && !selectedLeadId) startPendencies.push('Lead obrigatório (papel vendedor)');
  if (!generatedPlaybook) startPendencies.push('Playbook não preparado');
  const stepperCurrent = phase === 'prepare' ? 'prepare' : phase === 'finishing' ? 'feedback' : 'live';
  const stepperSteps = phase === 'finishing'
    ? [SESSION_STEPS[0], SESSION_STEPS[1], { ...SESSION_STEPS[2], icon: SpinningLoaderIcon }]
    : SESSION_STEPS;

  useEffect(() => {
    if (!sessionId) return;
    const node = liveTranscriptRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [events.length, sessionId]);

  return (
    <>
      {activePopup && (
        <div
          aria-live="assertive"
          className="fixed bottom-5 right-5 z-[70] w-[min(420px,calc(100vw-32px))] animate-in fade-in slide-in-from-bottom-3 overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-2xl ring-1 ring-emerald-100 duration-300 motion-reduce:animate-none"
        >
          <div className="flex items-start justify-between gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 text-white">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-emerald-100">
                <Sparkles className="h-4 w-4" />
                {activePopup.signal_type === 'objection' ? 'Objeção detectada' : 'Tema para abordar'}
              </p>
              <p className="mt-0.5 truncate text-sm font-bold">{activePopup.topic || activePopup.objection_key || 'Sinal detectado'}</p>
            </div>
            <button
              type="button"
              onClick={() => setActivePopup(null)}
              aria-label="Fechar sinal"
              title="Fechar sinal"
              className="rounded-md p-1.5 text-white/90 transition-colors hover:bg-white/20 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3 p-4">
            <p className="text-xs italic text-slate-500">Cliente: "{activePopup.phrase}"</p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Resposta sugerida</p>
              <p className="mt-1 text-[15px] font-semibold leading-relaxed text-slate-950">{activePopup.suggested_question || activePopup.fixed_response}</p>
            </div>
            {activePopup.suggested_action && (
              <p className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">Ação: {activePopup.suggested_action}</p>
            )}
            {activePopup.pain_diagnosis && (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">Dor: {activePopup.pain_diagnosis}</p>
            )}
            <div>
              <div className="flex items-center justify-between text-[11px] font-semibold text-emerald-700">
                <span>Confiança</span>
                <span>{(activePopup.confidence * 100).toFixed(0)}%</span>
              </div>
              <ConfidenceBar value={activePopup.confidence} />
            </div>
          </div>
        </div>
      )}

      <ModuleShell
        pageId="call-copilot"
        title="Iniciar conversa"
        subtitle="Prepare a conversa, acompanhe sinais relevantes em tempo real e receba a próxima pergunta ou ação para avançar."
        icon={Mic}
        actions={[{ id: 'nova-agenda', label: 'Nova agenda', icon: RefreshCw, variant: 'outline', size: 'sm', onClick: startNewAgenda, disabled: sessionLocked }]}
        headerExtra={<AgentStatusChip status={agentStatus} detail={agentDetail} />}
        stepper={<StepperSection steps={stepperSteps} current={stepperCurrent} />}
      >
        <Card className={cn('shadow-sm', agentStatus === 'offline' ? 'border-rose-200 bg-rose-50/60' : 'border-slate-200 bg-white')}>
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            {agentStatus === 'offline' ? (
              <AlertCircle className="h-5 w-5 shrink-0 text-rose-500" />
            ) : agentStatus === 'online' || agentStatus === 'recording' ? (
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
            ) : agentStatus === 'paused' ? (
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
            ) : (
              <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-slate-400" />
            )}
            {agentStatus === 'checking' ? (
              <Skeleton className="h-4 w-48" />
            ) : (
              <p className="min-w-0 text-sm font-medium text-slate-700">{agentDetail || 'Verificando agente local'}</p>
            )}
            <code className="hidden text-[11px] text-slate-400 sm:inline">{AGENT_URL}</code>
            <div className="ml-auto flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={checkAgent}
                disabled={agentStatus === 'checking'}
                aria-label="Verificar agente local"
                title="Verificar agente local"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant={agentStatus === 'offline' ? 'default' : 'outline'}
                size="sm"
                onClick={downloadAgent}
                className={agentStatus === 'offline' ? EMERALD_CTA_CLASSES : undefined}
              >
                <Download className="h-4 w-4" />
                Baixar instalador do agente
              </Button>
            </div>
          </CardContent>
        </Card>

        {phase === 'prepare' && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
            <Card className="min-w-0 border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <div className="min-w-0">
                  <CardTitle className="text-base">Agenda da conversa</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Cada agenda cria uma sessão independente e um playbook próprio.</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-5 pt-4">
                <div className="space-y-2">
                  <SectionLabel icon={User}>Contexto</SectionLabel>
                  {role === 'seller' ? (
                    <div className="space-y-1.5">
                      <Label>Lead associado <span className="text-rose-500">*</span></Label>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          className="pl-9 pr-3"
                          value={selectedLead ? `${selectedLead.name}${selectedLead.company ? ` · ${selectedLead.company}` : ''}` : leadSearch}
                          onChange={(e) => { setLeadSearch(e.target.value); setSelectedLeadId(''); setGeneratedPlaybook(null); setLeadPickerOpen(true); }}
                          onFocus={() => setLeadPickerOpen(true)}
                          onBlur={() => window.setTimeout(() => setLeadPickerOpen(false), 150)}
                          placeholder="Buscar por nome, empresa ou e-mail"
                          disabled={sessionLocked}
                        />
                        {leadPickerOpen && !sessionLocked && (
                          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                            {filteredLeads.length === 0 ? (
                              <p className="px-3 py-4 text-center text-xs text-slate-500">Nenhum lead encontrado.</p>
                            ) : filteredLeads.map((lead) => (
                              <button
                                key={lead.id}
                                type="button"
                                className={cn(
                                  'block w-full rounded-md border px-3 py-2 text-left transition-colors',
                                  selectedLeadId === lead.id ? 'border-emerald-200 bg-emerald-50' : 'border-transparent hover:bg-slate-50'
                                )}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { setSelectedLeadId(lead.id); setLeadSearch(''); setLeadPickerOpen(false); setGeneratedPlaybook(null); }}
                              >
                                <span className="block text-sm font-medium text-slate-900">{lead.name}</span>
                                <span className="block truncate text-xs text-slate-500">{[lead.company, lead.email].filter(Boolean).join(' · ') || 'Lead sem empresa cadastrada'}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedLead && (
                        <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                          <span>Lead selecionado: <strong>{selectedLead.name}</strong></span>
                          <button type="button" className="font-semibold underline" onClick={() => { setSelectedLeadId(''); setLeadSearch(''); setGeneratedPlaybook(null); }}>Trocar</button>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">O funil e a etapa atual serão vinculados automaticamente.</p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-sky-100 bg-sky-50 p-3 text-sm text-sky-800">
                      <strong>{roleLabel(role)} não usa lead do CRM.</strong>
                      <p className="mt-1 text-xs text-sky-700">O copiloto será orientado pelo escopo e pelos participantes.</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <SectionLabel>Direção</SectionLabel>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Objetivo</Label>
                      <select
                        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={objective}
                        onChange={(e) => { setObjective(e.target.value); setSelectedPlaybookId(''); setGeneratedPlaybook(null); }}
                        disabled={sessionLocked}
                      >
                        {allowedObjectives.map((key) => <option key={key} value={key}>{objectiveLabel(key)}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Seu papel</Label>
                      <select
                        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                        value={role}
                        onChange={(e) => {
                          const nextRole = e.target.value;
                          setRole(nextRole);
                          setObjective(OBJECTIVES_BY_ROLE[nextRole]?.[0] || 'negotiation');
                          if (nextRole !== 'seller') { setSelectedLeadId(''); setLeadSearch(''); }
                          setSelectedPlaybookId('');
                          setGeneratedPlaybook(null);
                        }}
                        disabled={sessionLocked}
                      >
                        {(playbooks.roles.length ? playbooks.roles : Object.entries(ROLE_LABELS).map(([key, name]) => ({ key, name }))).map((item) => <option key={item.key} value={item.key}>{item.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1.5 pt-1">
                    <Label>Objetivo cadastrado / playbook</Label>
                    <select
                      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={selectedPlaybookId}
                      onChange={(e) => { setSelectedPlaybookId(e.target.value); setGeneratedPlaybook(null); }}
                      disabled={sessionLocked}
                    >
                      <option value="">Usar orientação padrão da IA</option>
                      {availablePlaybooks.map((item) => <option key={item.id} value={item.id}>{item.objective_name || item.name}</option>)}
                    </select>
                    <p className="text-xs text-slate-500">
                      {availablePlaybooks.length ? 'O playbook selecionado será usado como orientação permanente.' : 'Nenhum playbook personalizado cadastrado para este papel e objetivo.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <SectionLabel>Escopo</SectionLabel>
                  <div className="space-y-1.5">
                    <Label>Escopo ou agenda da conversa <span className="text-xs font-normal text-slate-400">(opcional)</span></Label>
                    <Textarea
                      rows={3}
                      value={meetingScope}
                      onChange={(e) => { setMeetingScope(e.target.value); setGeneratedPlaybook(null); }}
                      disabled={sessionLocked}
                      placeholder="Ex.: decidir prioridades do trimestre, revisar bloqueios e sair com responsáveis."
                    />
                    <p className="text-xs text-slate-500">A IA usa este contexto para priorizar os sinais. Se ficar vazio, usa o objetivo e o playbook selecionados.</p>
                  </div>
                </div>

                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <Button
                    type="button"
                    size="lg"
                    onClick={startSession}
                    disabled={sessionLocked || agentStatus === 'offline' || !generatedPlaybook || preparingPlaybook || isFinishing}
                    className={cn('h-12 w-full text-base', EMERALD_CTA_CLASSES)}
                  >
                    <Play className="h-5 w-5" />
                    Iniciar conversa
                  </Button>
                  <div className="mt-3 flex flex-col gap-1.5">
                    {startPendencies.length > 0 ? (
                      startPendencies.map((item) => (
                        <span key={item} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                          {item}
                        </span>
                      ))
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                        Tudo pronto para iniciar a captura
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="h-fit border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-4">
                <div className="min-w-0">
                  <CardTitle className="text-base">Copiloto de IA</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Playbook, áudio e ferramentas de apoio à sessão.</p>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <Accordion type="multiple" defaultValue={['playbook']}>
                  <AccordionItem value="playbook" className="border-b border-slate-100">
                    <AccordionTrigger className="py-3 hover:no-underline">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <WandSparkles className="h-4 w-4 text-violet-700" />
                        Playbook da sessão
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <div className="rounded-xl border border-violet-200 bg-violet-50 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-xs leading-5 text-violet-800">
                            {generatedPlaybook
                              ? `${generatedPlaybook.topics?.length || 0} tópicos preparados${generatedPlaybook.playbook_name ? ` a partir de “${generatedPlaybook.playbook_name}”` : ''}.`
                              : 'A IA ainda precisa transformar o escopo em perguntas e gatilhos.'}
                          </p>
                          {generatedPlaybook?.source === 'ai' && (
                            <Badge className="shrink-0 border border-violet-200 bg-violet-50 text-violet-700">gerado pela IA</Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={preparePlaybook}
                          disabled={preparingPlaybook || sessionLocked || isFinishing}
                          className="mt-3 w-full border-violet-300 bg-white text-violet-800 shadow-sm hover:bg-violet-50 hover:text-violet-900"
                        >
                          {preparingPlaybook ? <Loader2 className="h-4 w-4 animate-spin" /> : <WandSparkles className="h-4 w-4" />}
                          {preparingPlaybook ? 'Preparando...' : generatedPlaybook ? 'Repreparar' : 'Preparar com IA'}
                        </Button>
                        {generatedPlaybook?.topics?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {generatedPlaybook.topics.slice(0, 6).map((topic) => (
                              <Badge key={topic.key || topic.name} className="border border-violet-200 bg-white text-violet-800">{topic.name}</Badge>
                            ))}
                          </div>
                        )}
                        {generatedPlaybook?.opening_questions?.length > 0 && (
                          <div className="mt-4 rounded-lg border border-violet-200 bg-white p-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">Perguntas para abrir a conversa</p>
                            <ul className="mt-2 space-y-2 text-xs leading-5 text-slate-700">
                              {generatedPlaybook.opening_questions.slice(0, 5).map((question, index) => (
                                <li key={`${question}-${index}`} className="flex gap-2"><span className="font-semibold text-violet-600">{index + 1}.</span><span>{question}</span></li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="audio" className="border-b border-slate-100">
                    <AccordionTrigger className="py-3 hover:no-underline">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <MonitorSpeaker className="h-4 w-4 text-slate-500" />
                        Áudio e dispositivos
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label>Seu microfone</Label>
                          <select
                            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                            value={sellerDeviceId}
                            onChange={(e) => setSellerDeviceId(e.target.value)}
                            disabled={sessionLocked}
                          >
                            <option value="">Microfone padrão</option>
                            {audioDevices.microphones.filter((device) => !device.loopback).map((device) => (
                              <option key={device.id} value={device.id}>
                                {device.name}{device.default ? ' (padrão)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Áudio do cliente</Label>
                          <select
                            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                            value={customerDeviceId}
                            onChange={(e) => setCustomerDeviceId(e.target.value)}
                            disabled={sessionLocked}
                          >
                            <option value="">Loopback padrão do Windows</option>
                            {audioDevices.microphones.filter((device) => device.loopback).map((device) => (
                              <option key={device.id} value={device.id}>
                                {device.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <p className="text-xs text-slate-500">O áudio do cliente captura o loopback do sistema (o que toca nas caixas de som).</p>
                        {audioDevices.microphones.length === 0 && (
                          <p className="text-xs text-slate-400">Nenhum dispositivo listado ainda — verifique o agente local para recarregar a lista.</p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="test-mode" className="border-b-0">
                    <AccordionTrigger className="py-3 hover:no-underline">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <FlaskConical className="h-4 w-4 text-amber-600" />
                        Modo teste
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-1">
                      <div className="space-y-2">
                        <Button type="button" variant="outline" className="w-full" onClick={() => emitTestTranscript('customer')} disabled={!sessionId}>
                          Testar cliente
                        </Button>
                        <Button type="button" variant="outline" className="w-full" onClick={() => emitTestTranscript('seller')} disabled={!sessionId}>
                          Testar vendedor
                        </Button>
                        <p className="text-xs text-slate-500">Requer sessão iniciada.</p>
                        <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700">Dispara falas sintéticas para validar a detecção de sinais.</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>
        )}

        {phase === 'prepare' && (
          <EmptyState
            compact
            icon={Sparkles}
            title="Prepare a agenda e inicie a captura"
            description="O copiloto escuta a conversa, detecta objeções e sugere a próxima pergunta ou ação em tempo real."
            action={{ label: 'Ver feedbacks anteriores', onClick: () => onNavigate?.('call-feedback') }}
          />
        )}

        {phase === 'live' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-2 sm:flex-row">
              {sessionState === 'recording' ? (
                <Button type="button" variant="outline" size="lg" onClick={pauseSession} disabled={sessionState !== 'recording'} className="sm:flex-1">
                  <Pause className="h-4 w-4" />
                  Pausar
                </Button>
              ) : (
                <Button type="button" size="lg" onClick={pauseSession} disabled={sessionState !== 'paused'} className={cn('sm:flex-1', EMERALD_CTA_CLASSES)}>
                  <Play className="h-4 w-4" />
                  Retomar
                </Button>
              )}
              <Button type="button" variant="destructive" size="lg" onClick={stopSession} disabled={!sessionId} className="sm:flex-1">
                <Square className="h-4 w-4" />
                Finalizar e gerar feedback
              </Button>
            </div>
          </div>
        )}

        {phase === 'live' && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Card className="min-w-0 border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    {sessionState === 'paused' ? (
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    ) : (
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75 motion-reduce:animate-none" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
                      </span>
                    )}
                    <CardTitle className="text-base">Transcrição ao vivo</CardTitle>
                    <SessionTimer state={sessionState} />
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="secondary">{events.length} falas</Badge>
                    <Badge variant="secondary">{cards.length} sinais</Badge>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold',
                        classifying ? 'animate-pulse border-sky-100 bg-sky-50 text-sky-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      )}
                    >
                      <Cpu className="h-3.5 w-3.5" />
                      {classifying ? 'Analisando...' : 'JEV pronto'}
                    </span>
                    {persistenceChip && (
                      <span className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold', persistenceChip.classes)}>
                        {persistenceChip.label}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {sessionState === 'paused' && (
                  <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Sessão pausada — a captura e a análise estão suspensas.
                  </p>
                )}
                <div ref={liveTranscriptRef} role="log" aria-live="polite" className="flex max-h-[560px] flex-col gap-3 overflow-y-auto pr-1">
                  {events.length === 0 ? (
                    <EmptyState
                      compact
                      icon={Mic}
                      title="Nenhuma fala capturada ainda."
                      description="As falas do vendedor e do cliente aparecem aqui em tempo real."
                    />
                  ) : chronologicalEvents.map((event, index) => {
                    if (event.speaker === 'seller') {
                      return (
                        <div key={`${event.created_at}-${index}`} className="flex animate-in fade-in slide-in-from-bottom-1 gap-2 duration-300 motion-reduce:animate-none">
                          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                            <Mic className="h-3.5 w-3.5" />
                          </span>
                          <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-emerald-100 bg-emerald-50/60 px-3.5 py-2.5">
                            <p className="text-sm leading-relaxed text-slate-800">{event.text}</p>
                            <p className="mt-1 text-[10px] text-slate-400">Vendedor · {formatTime(event.created_at)}</p>
                          </div>
                        </div>
                      );
                    }
                    if (event.speaker === 'customer') {
                      return (
                        <div key={`${event.created_at}-${index}`} className="flex animate-in fade-in slide-in-from-bottom-1 justify-end gap-2 duration-300 motion-reduce:animate-none">
                          <div className="max-w-[85%] rounded-2xl rounded-tr-sm border border-blue-100 bg-blue-50/70 px-3.5 py-2.5">
                            <p className="text-sm leading-relaxed text-slate-800">{event.text}</p>
                            <p className="mt-1 text-right text-[10px] text-slate-400">Cliente · {formatTime(event.created_at)}</p>
                          </div>
                          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-blue-100 text-blue-700">
                            <User className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div key={`${event.created_at}-${index}`} className="mx-auto w-fit max-w-[85%] animate-in fade-in slide-in-from-bottom-1 rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 duration-300 motion-reduce:animate-none">
                        <p className="text-sm leading-relaxed text-slate-600">{event.text}</p>
                        <p className="mt-1 text-center text-[10px] text-slate-400">Indefinido · {formatTime(event.created_at)}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0 border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">Sinais ao vivo</CardTitle>
                  <Badge variant="secondary">{cards.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                {cards.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Sparkles}
                    title="Nenhum sinal detectado ainda"
                    description="A IA sinaliza objeções e temas assim que o cliente fala. Use o Modo teste para validar."
                  />
                ) : (
                  <>
                    <div className="animate-in fade-in slide-in-from-right-3 rounded-xl border-2 border-emerald-300 bg-white p-4 shadow-md duration-300 motion-reduce:animate-none">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge className="bg-emerald-600 text-white">{latestCard.signal_type === 'objection' ? 'Objeção' : 'Tema'}</Badge>
                        <span className="text-[11px] text-slate-400">{formatTime(latestCard.created_at)}</span>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">Cliente: "{latestCard.phrase}"</p>
                      <p className="mt-1.5 text-[15px] font-semibold leading-relaxed text-slate-950">
                        {latestCard.suggested_question || latestCard.fixed_response}
                      </p>
                      {latestCard.suggested_action && (
                        <p className="mt-3 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">Ação: {latestCard.suggested_action}</p>
                      )}
                      {latestCard.pain_diagnosis && (
                        <p className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">Dor: {latestCard.pain_diagnosis}</p>
                      )}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] font-semibold text-emerald-700">
                          <span>Confiança</span>
                          <span>{(latestCard.confidence * 100).toFixed(0)}%</span>
                        </div>
                        <div className="mt-1">
                          <ConfidenceBar value={latestCard.confidence} />
                        </div>
                      </div>
                    </div>
                    {cards.length > 1 && (
                      <div className="space-y-2">
                        <SectionLabel>Histórico</SectionLabel>
                        {cards.slice(1).map((card) => (
                          <div key={card.id} className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <Badge variant="secondary">{card.topic || card.objection_key || 'Sinal'}</Badge>
                              <span className="text-[11px] font-medium text-emerald-700">confiança {(card.confidence * 100).toFixed(0)}%</span>
                            </div>
                            <p className="mt-1.5 line-clamp-2 text-sm font-medium text-slate-800">{card.suggested_question || card.fixed_response}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {phase === 'finishing' && (
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-violet-700" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-violet-900">
                    Finalizando a sessão e gerando o feedback… A tela Feedback de conversas abrirá automaticamente.
                  </p>
                  <p className="mt-1 text-xs text-violet-700">
                    Sessão capturou {events.length} {events.length === 1 ? 'fala' : 'falas'} e {cards.length} {cards.length === 1 ? 'sinal' : 'sinais'}.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate?.('call-feedback')}
                  disabled={isFinishing}
                  className="shrink-0 border-violet-300 bg-white text-violet-800 shadow-sm hover:bg-violet-50 hover:text-violet-900"
                >
                  Abrir Feedback de conversas
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </ModuleShell>
    </>
  );
}
